// SpotBidding 通常入札タブの「札 1 行」 を表す frontend 表示型。
// proto `BiddingSpotBid` と親 approval request の値を表示向けに整形して保持する。
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type {
  BiddingBidSendErrorCategory,
  BiddingBidSendStatus,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import {
  BiddingApprovalState,
  BiddingSpotBidStatus,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type { BiddingSpotBidOrigin } from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'
import { BiddingSpotOrderMethod } from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'

import type { BidIssuerType } from '~/models/bidIssuerType'

/** 発注主体 (= 札発行元の業務区分)。 */
// proto 側は共通 enum `LicenseType` (= BiddingSpotBid.issuer_type、 #6069 で供給開始)。
// 表示行では string union で持ち、 変換は ~/models/bidIssuerType の bidIssuerTypeOf が行う。
export type SpotBidIssuerType = BidIssuerType

/** 札 1 行の表示型。 */
export type SpotBidRow = {
  // ----- proto から来る field -----
  id: string
  approvalRequestId: string
  areaCode: AreaCode
  /** コマ番号 1〜48。 */
  timeCode: number
  direction: Side
  /**
   * 価格種別 (指値 / 成行、 `spot-bid-normal-csv-import.md` AC-SPOT-006)。
   *
   * 通常入札タブ (GetBiddingSpotApprovalRequest 由来) から供給される。
   * 判定は [[isMarketOrderRow]] を通すこと (undefined / UNSPECIFIED = 指値扱い。
   * = backend converter の境界解決と同じ規約。 optional のままなのは旧データ /
   * 未設定時の防御)。
   */
  orderMethod?: BiddingSpotOrderMethod
  /**
   * 入札価格 円/kWh。 proto google.type.Decimal は string で受ける (= shopspring/decimal 互換)。
   * 成行 (`orderMethod === MARKET`) の札は価格を持たず "0" を運ぶ (AC-SPOT-006)。
   * この "0" は指値 0 円ではないので、 価格レンジ判定・価格表示は必ず
   * [[isMarketOrderRow]] で成行を除いてから行う。
   */
  priceYenPerKwh: string
  /** 入札量 (コマ別 MW。 列名 volume_mwh は歴史的経緯、 issue #4285)。 */
  volumeMwh: string
  /** AC-BSP-125: 取り込み元の観測。業務状態とは独立。 */
  needsReview?: boolean
  needsReviewSince?: Date
  bidStatus: BiddingSpotBidStatus
  jepxBidNo?: string

  // ----- 親 approval request 由来 (= 表示用に展開) -----
  approvalState: BiddingApprovalState

  /**
   * 送信軸の状態 (= eSLJ への外部送信ステータス: 送信待ち / 送信中 / 提出済 / 送信失敗)。
   * 承認軸 (approvalState) / 約定軸 (bidStatus) と直交する第 3 軸。
   *
   * 供給元は承認依頼 (`BiddingSpotApprovalRequest.send_status`)。 提出は承認依頼単位の
   * 一括処理なので、 同じ申請セットの札が全員同じ値を持つ。 締切までに JEPX へ
   * 届かなかった札 (SEND_FAILED_FINAL) はこの軸でしか判別できない
   * (承認軸は SUBMITTED のまま)。
   */
  sendStatus?: BiddingBidSendStatus

  /**
   * 送信失敗の理由カテゴリ / 生 detail (= sendStatus が失敗系のときのみ意味を持つ)。
   * sendStatus 同様、 承認依頼から供給される。
   */
  sendErrorCategory?: BiddingBidSendErrorCategory
  sendErrorDetail?: string

  // ----- 表示用に整形・補完する field -----

  /**
   * 発注主体 (GetBiddingSpotApprovalRequest 由来)。 供給されない札は undefined になる。
   * **undefined を「発電」等の既定値で埋めない** (= 嘘の値を表示しない。 #6094)。
   *
   * 'unknown' は区分が空欄の取り込んだ札 (広域マスタに無い受渡契約コード、 AC-BSP-111b)。
   * 入札一覧はツールバーの区分でスコープされるが、 この札はどの区分を選んでいても一覧に
   * 残る (AC-BSP-019 ※。 [[isInPageIssuerScope]])。
   */
  issuerType?: SpotBidIssuerType

  /**
   * トランシェ ID。 「トランシェ構成」 タブ (Phase 2) で定義される入札グループ。
   * TODO(proto): BiddingSpotBid.tranche_id (string、 optional) を追加する。
   */
  trancheId?: string

  /**
   * 約定価格 円/kWh (= proto `BiddingSpotBid.matched_price_yen_per_kwh`)。
   * 一斉約定後に eSLJ から取得した値で、 約定待ち / 不約定では未設定。
   */
  execPriceYenPerKwh?: string

  /**
   * 約定量 (コマ別 MW。 = proto `BiddingSpotBid.matched_volume_mwh`。 field 名の
   * mwh は歴史的経緯、 issue #4285)。 部分約定では volume_mwh より小さい。
   * 約定待ち / 不約定では未設定。
   */
  execVolumeMwh?: string

  /**
   * メモ（JEPX提出用）。 JEPX へ note として送信される記入欄
   * (= proto `BiddingSpotBid.memo_jepx`)。
   */
  memoJepx?: string

  /**
   * メモ（自由記述）。 社内限定で JEPX へは送信しない
   * (= proto `BiddingSpotBid.memo_free`。 旧 `memo` を rename)。
   */
  memoFree?: string

  /**
   * 作成日時 (= JST、 design HTML の「作成」 列で表示)。
   * proto `BiddingSpotBid.created_at` (監査情報) を表示用に `Date` 化した値。
   */
  createdAt: Date

  /**
   * 受渡日。 親 approval_request の target_date を 1 札に展開した値
   * (`convertSpotBidsFromApprovalRequest` が転記する。 `BiddingSpotBid` 自身は持たない)。
   */
  targetDate?: Date

  /**
   * 作成者 (eauth member UUID)。 proto `BiddingSpotBid.created_by`。
   */
  createdBy?: string

  /**
   * 受渡契約コード (= proto `BiddingSpotBid.delivery_contract_code`、
   * AC-BSP-001)。 発注主体区分 × 入札エリアから広域マスタを自動解決した値
   * (backend `SpotBidUsecase` が Create/Update 時に persist する。 T-74)。
   * ユーザーの自由入力ではない。
   *
   * この列が追加される前に作成された札は未設定 (空文字) になりうる。
   */
  deliveryContractCode?: string

  /**
   * ワークフロー経由で自動作成された入札の紐付け ID (= proto
   * `BiddingSpotBid.workflow_id`、 AC-BSP-001)。 自動入札ルールが作った札
   * (AC-BSP-060) は発火した業務ワークフローの ID を持ち、 手動作成は未設定
   * (= 列自体は spec が要求するため常設し、 空表示で構わない)。
   */
  workflowId?: string

  /**
   * 札に保存された起票由来 (= proto `BiddingSpotBid.origin`、 AC-BSP-103)。
   * 承認申請時の入札制限判定 (`spotBidValidation.ts`) はこの値を見ず、
   * 全札に上限数量(1札) を適用する。
   */
  origin?: BiddingSpotBidOrigin

  /**
   * 応答時点で失効が確定しているか (= proto `BiddingSpotBid.expiry_confirmed`、
   * AC-BSP-117)。 翌営業日向けの再利用 (複製、 AC-BSP-011) と
   * 締切超過の案内を出してよいかは backend が決め、 画面はこの値を読むだけにする
   * ([[isExpiryConfirmed]])。
   */
  expiryConfirmed?: boolean

  /**
   * 応答時点で JEPX が受け付けていたかを確かめている札か (= proto
   * `BiddingSpotBid.receipt_checking`、
   * AC-BSP-117)。 送信失敗 (確定) のまま入札締切を迎えた札は、 確かめるまで締切の後も
   * ステータス列を 「送信中」 のまま出す ([[deriveBidStatusCategory]])。 確かめている
   * かは backend が決め、 画面は送信軸から再導出しない (backend の確認と食い違うと、
   * JEPX が受け付けていた札を失効と表示してしまうため)。
   */
  receiptChecking?: boolean

  /**
   * 応答時点でこの札の取下 (AC-BSP-057) を受け付けるか (= proto
   * `BiddingSpotBid.withdrawable`)。 backend が WithdrawSpotBid と同じ述語で決め、
   * 画面は承認軸 / 送信軸 / 約定軸から再導出しない ([[resolveSpotBidWithdrawAction]])。
   */
  withdrawable?: boolean

  /**
   * 応答時点でこの札を「下書きに戻す」 (AC-BSP-117 / AC-BSP-129) で戻すか
   * (= proto `BiddingSpotBid.returnable_to_draft`)。 札ごとの答えで、 申請セットが戻せて札が
   * 送信中の時だけ true (入札中の札は同じ申請セットでも false)。 backend が決め、 画面は
   * 再導出しない ([[canReturnToDraft]])。
   */
  returnableToDraft?: boolean
}

/**
 * 承認申請・提出・全削除の対象にできる下書きの札か (AC-BSP-007)。 入札締切まで提出せず失効にした札は
 * 承認ステータスが N/A (下書きと同じ値) に戻るので、 約定軸が失効の札は含めない (AC-BSP-099)。
 */
export const isNonExpiredDraftSpotBid = (
  row: Pick<SpotBidRow, 'approvalState' | 'bidStatus'>,
): boolean =>
  row.approvalState === BiddingApprovalState.DRAFT &&
  row.bidStatus !== BiddingSpotBidStatus.EXPIRED

/**
 * 札が成行 (= 価格を指定しない注文、 AC-SPOT-006) かを判定する唯一の口。
 *
 * `undefined` (= field 未設定の防御的ケース) と `UNSPECIFIED`
 * (= order_method を送らない旧クライアントが作った札) はどちらも指値扱いにする。
 * backend converter (`spotBidOrderMethodFromProto`) が同じ境界解決をするので、
 * client と server で成行判定が食い違わない。
 */
export const isMarketOrderRow = (row: {
  orderMethod?: BiddingSpotOrderMethod
}): boolean => row.orderMethod === BiddingSpotOrderMethod.MARKET

/** トランシェ master の placeholder。 TODO(proto): 「トランシェ構成」 RPC は Phase 2 で実装。 */
export type SpotBidTranche = {
  id: string
  name: string
}
